import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import WorkshopForm from './components/WorkshopForm';
import WorkshopsList from './components/WorkshopsList';
import AnnouncementsList from './components/AnnouncementsList';
import AnnouncementForm from './components/AnnouncementForm';

// Admin email - only this email has access
const ADMIN_EMAIL = 'clubunisteps@gmail.com';

const AdminPanel = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingWorkshop, setEditingWorkshop] = useState(null);
  const [workshops, setWorkshops] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [error, setError] = useState(null);
  
  // Announcement management state
  const [viewingAnnouncements, setViewingAnnouncements] = useState(null);
  const [showAnnouncementForm, setShowAnnouncementForm] = useState(false);

  // Check if user is admin (only clubunisteps@gmail.com)
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        // Check if user email matches the admin email
        const isAdminUser = user.email === ADMIN_EMAIL;
        
        if (!isAdminUser) {
          console.warn('Unauthorized access attempt to admin panel');
          setIsAdmin(false);
          setLoading(false);
          // Redirect to homepage after a brief moment
          setTimeout(() => navigate('/'), 500);
          return;
        }

        setIsAdmin(true);
      } catch (err) {
        console.error('Error:', err);
        setError(err.message || 'Failed to verify admin status');
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [user, navigate]);

  // Fetch workshops
  useEffect(() => {
    const fetchWorkshops = async () => {
      try {
        setError(null);
        const { data, error } = await supabase
          .from('workshops')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) throw error;
        setWorkshops(data || []);
      } catch (err) {
        console.error('Error fetching workshops:', err);
        setError('Failed to load workshops: ' + err.message);
      }
    };

    if (isAdmin) {
      fetchWorkshops();
    }
  }, [isAdmin, refreshKey]);

  const handleEdit = (workshop) => {
    setEditingWorkshop(workshop);
    setShowForm(true);
  };

  const handleDelete = async (workshopId) => {
    if (!confirm('Are you sure you want to delete this workshop?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('workshops')
        .delete()
        .eq('id', workshopId);

      if (error) throw error;
      
      setRefreshKey(prev => prev + 1);
      alert('Workshop deleted successfully!');
    } catch (err) {
      console.error('Error deleting workshop:', err);
      alert('Failed to delete workshop: ' + err.message);
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingWorkshop(null);
    setRefreshKey(prev => prev + 1);
  };

  const handleManageAnnouncements = (workshop) => {
    setViewingAnnouncements(workshop);
  };

  const handleBackFromAnnouncements = () => {
    setViewingAnnouncements(null);
    setShowAnnouncementForm(false);
  };

  const handleAddAnnouncement = () => {
    setShowAnnouncementForm(true);
  };

  const handleAnnouncementSuccess = () => {
    setShowAnnouncementForm(false);
    // Refresh announcements list
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (error && !isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <Icon name="AlertCircle" className="w-16 h-16 text-destructive mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Error</h1>
          <p className="text-muted-foreground mb-6">
            {error}
          </p>
          <Button onClick={() => navigate('/')}>
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <Icon name="ShieldAlert" className="w-16 h-16 text-destructive mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
          <p className="text-muted-foreground mb-6">
            You don't have permission to access the admin panel.
          </p>
          <Button onClick={() => navigate('/')}>
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Admin Panel - Workshop Management</title>
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="container mx-auto px-4 py-8 max-w-7xl pt-28">
          {/* Show announcements view if viewing announcements */}
          {viewingAnnouncements ? (
            <>
              {/* Announcements List or Form */}
              {showAnnouncementForm ? (
                <div className="bg-background rounded-lg border border-border max-w-4xl mx-auto">
                  <AnnouncementForm
                    workshop={viewingAnnouncements}
                    onClose={() => setShowAnnouncementForm(false)}
                    onSuccess={handleAnnouncementSuccess}
                  />
                </div>
              ) : (
                <div className="bg-background rounded-lg border border-border">
                  <AnnouncementsList
                    workshop={viewingAnnouncements}
                    onBack={handleBackFromAnnouncements}
                    onAddNew={handleAddAnnouncement}
                  />
                </div>
              )}
            </>
          ) : (
            <>
              {/* Header */}
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h1 className="text-3xl font-bold mb-2">Workshop Management</h1>
                  <p className="text-muted-foreground">
                    Create and manage workshops for the platform
                  </p>
                </div>
                <Button
                  onClick={() => {
                    setEditingWorkshop(null);
                    setShowForm(true);
                  }}
                  className="flex items-center gap-2"
                >
                  <Icon name="Plus" className="w-5 h-5" />
                  Create New Workshop
                </Button>
              </div>

              {/* Workshop Form Modal */}
              {showForm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                  <div className="bg-background rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                    <WorkshopForm
                      workshop={editingWorkshop}
                      onClose={handleFormClose}
                    />
                  </div>
                </div>
              )}

              {/* Error alert */}
              {error && (
                <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 mb-6">
                  <p className="text-destructive font-medium">Error: {error}</p>
                </div>
              )}

              {/* Workshops List */}
              <WorkshopsList
                workshops={workshops}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onManageAnnouncements={handleManageAnnouncements}
              />
            </>
          )}
        </main>
      </div>
    </>
  );
};

export default AdminPanel;
